import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PaymentMethodType } from "@/core";
import { useRecordPayment } from "@/hooks";
import { useState } from "react";

const getToday = () => new Date().toISOString().split("T")[0];

interface RecordPaymentFormProps {
  appliesToType: "po" | "invoice";
  appliesToId: string;
  defaultMethod?: PaymentMethodType;
  onSuccess?: () => void;
}

export function RecordPaymentForm(props: Readonly<RecordPaymentFormProps>) {
  const { appliesToType, appliesToId, defaultMethod, onSuccess } = props;
  const recordPaymentMutation = useRecordPayment();

  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<PaymentMethodType>(defaultMethod ?? "bank");
  const [date, setDate] = useState(getToday);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [scheduled, setScheduled] = useState(false);

  const handleSubmit = async () => {
    if (!amount || Number(amount) <= 0) return;
    try {
      await recordPaymentMutation.mutateAsync({
        appliesToType,
        appliesToId,
        amount: Number(amount),
        method,
        status: scheduled ? "pending" : "cleared",
        paidAt: date,
        referenceId: reference.trim() || undefined,
        notes: notes.trim() || undefined,
      });
    } catch {
      return;
    }
    setAmount("");
    setMethod(defaultMethod ?? "bank");
    setReference("");
    setNotes("");
    setScheduled(false);
    setDate(getToday());
    onSuccess?.();
  };

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label>Amount</Label>
          <Input
            type="number"
            min="0"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label>Method</Label>
          <Select
            value={method}
            onValueChange={(v) => setMethod(v as PaymentMethodType)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="paypal_goods_services">PayPal - Goods and Services</SelectItem>
              <SelectItem value="paypal_friends_family">PayPal - Friends and Family</SelectItem>
              <SelectItem value="paypal_goods_services_fees">PayPal - Goods and Services + Fees</SelectItem>
              <SelectItem value="bank">Bank</SelectItem>
              <SelectItem value="zelle">Zelle</SelectItem>
              <SelectItem value="card">Card</SelectItem>
              <SelectItem value="crypto">Crypto</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1">
        <Label>Date</Label>
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label>Reference (optional)</Label>
        <Input
          placeholder="Transaction ID, check number, etc."
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label>Notes (optional)</Label>
        <Textarea
          placeholder="Any additional notes..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
        />
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id="scheduled"
          checked={scheduled}
          onCheckedChange={(checked) => setScheduled(checked === true)}
        />
        <Label htmlFor="scheduled" className="cursor-pointer font-normal">
          Schedule for later (pending)
        </Label>
      </div>
      <Button
        onClick={handleSubmit}
        disabled={Number(amount) <= 0}
        loading={recordPaymentMutation.isPending}
        loadingText={scheduled ? "Scheduling..." : "Recording..."}
      >
        {scheduled ? "Schedule Payment" : "Record Cleared Payment"}
      </Button>
    </div>
  );
}
